import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../contexts/ApiContext';
import { createIdempotencyKey } from '../hooks/useIdempotencyKey';
import {
  ApiCardActivateRequest,
  ApiCardOrderIntentRequest,
  ApiCardOrderRequest,
  ApiCardRenameRequest,
  ApiCardUpdateSecurityRequest,
  ApiCardUpdateSpendingLimitRequest,
  ApiCardUpdateSpendingStrategyRequest,
} from '../openapi';
import { withWallesterEncryption } from '@razz-team/utils';
import { mapResponse } from './helpers';
import { useAccountsQuery } from './accounts';
import { useMemo } from 'react';
import { uniqBy } from 'lodash-es';

export function useCardListQuery() {
  const client = useApiClient();
  const accounts = useAccountsQuery();
  return useQuery({
    queryKey: ['cards'],
    queryFn: ({ signal }) =>
      client
        .GET('/cards', { signal })
        .then(mapResponse)
        .then(({ cards }) =>
          cards.map((it) => ({
            ...it,
            balance: accounts.data?.find((a) => a.id === it.spendingStrategy.accountId)?.balance,
          })),
        ),
  });
}

export function useCardsWithCardQuery(cardId: string | null | undefined) {
  const cardsQuery = useCardListQuery();
  const cardQuery = useCardQuery({ cardId });

  const data = useMemo(() => {
    if (cardsQuery.data && cardQuery.data) {
      return uniqBy([...cardsQuery.data, cardQuery.data], 'id');
    }

    return cardsQuery.data ?? [];
  }, [cardsQuery.data, cardQuery.data]);

  const isPending = cardQuery.isEnabled
    ? cardsQuery.isPending || cardQuery.isPending
    : cardsQuery.isPending;
  const error = cardQuery.isEnabled ? (cardsQuery.error ?? cardQuery.error) : cardsQuery.error;
  return {
    data,
    isPending,
    error,
    isEnabled: cardsQuery.isEnabled && cardQuery.isEnabled,
  };
}
export function useCardDesignListQuery() {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', 'design'],
    queryFn: ({ signal }) =>
      client
        .GET('/cards/designs', { signal })
        .then(mapResponse)
        .then(({ cardDesigns }) => cardDesigns),
  });
}

export function useCardSecurityQuery({ cardId: id }: { cardId: string }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', id, 'security'],
    queryFn: ({ signal }) =>
      client.GET('/cards/{id}/security', { signal, params: { path: { id } } }).then(mapResponse),
  });
}

export function useCardQuery({ cardId }: { cardId: string | null | undefined }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', cardId],
    queryFn: () =>
      client.GET('/cards/{id}', { params: { path: { id: cardId! } } }).then(mapResponse),
    enabled: Boolean(cardId),
    refetchInterval(query) {
      switch (query.state.data?.status) {
        case 'CREATED':
          return 3000;
        case 'ISSUED':
          return 5000;
        case 'ACTIVATING':
          return 1000;
        default:
          return -1;
      }
    },
  });
}

export function useCardIntentQuery(request: ApiCardOrderIntentRequest | undefined) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', 'order', 'intent', request],
    queryFn: ({ signal }) =>
      client
        .POST('/cards/order/intent', {
          signal,
          body: request!,
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    enabled: Boolean(request),
    retry: 1,
    retryDelay: 500,
  });
}

export function useCardOrderMutation(idempotencyKey: string) {
  const client = useApiClient();
  return useMutation({
    mutationKey: ['cards', 'order'],
    mutationFn: (body: ApiCardOrderRequest) =>
      client
        .POST('/cards/order', {
          body,
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })
        .then(mapResponse),
  });
}

export function useCardNumberQuery({
  cardId,
  enabled = true,
}: {
  cardId: string;
  enabled?: boolean;
}) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', cardId, 'number'],
    enabled,
    gcTime: 0,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const pan = await withWallesterEncryption('CardNumber', async (key) => {
        const res = await client.POST('/cards/{id}/number', {
          params: { path: { id: cardId } },
          signal,
          body: {
            key,
            issuerEncryptionType: 'RSA',
          },
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        });

        if (!res.data) {
          throw new Error('No data');
        }

        return res.data.pan;
      });

      return { pan };
    },
  });
}

export function useCardCvvQuery({ cardId, enabled = true }: { cardId: string; enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', cardId, 'cvv'],
    enabled,
    gcTime: 0,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const cvv = await withWallesterEncryption('CVV2', async (key) => {
        const res = await client.POST('/cards/{id}/cvv', {
          params: { path: { id: cardId } },
          signal,
          body: {
            key,
            issuerEncryptionType: 'RSA',
          },
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        });

        if (!res.data) {
          throw new Error('No data');
        }

        return res.data.cvv;
      });

      return { cvv };
    },
  });
}

export function useCardCredentialsUri({
  cardId,
  enabled = true,
}: {
  cardId: string;
  enabled?: boolean;
}) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', cardId, 'credentials-uri'],
    queryFn: ({ signal }) =>
      client
        .POST('/cards/{id}/credentialsUri', {
          signal,
          params: { path: { id: cardId } },
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
    enabled,
  });
}

export function useCardPinUri({ cardId, enabled = true }: { cardId: string; enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', cardId, 'pin-uri'],
    queryFn: ({ signal }) =>
      client
        .POST('/cards/{id}/pinUri', {
          signal,
          params: { path: { id: cardId } },
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
    enabled,
  });
}

export function useCardSetPinUri({
  cardId,
  enabled = true,
}: {
  cardId: string;
  enabled?: boolean;
}) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', cardId, 'set-pin-uri'],
    queryFn: ({ signal }) =>
      client
        .POST('/cards/{id}/setPinUri', {
          signal,
          params: { path: { id: cardId } },
          headers: { 'Idempotency-Key': createIdempotencyKey() },
        })
        .then(mapResponse),
    enabled,
  });
}

export function useCardPinQuery({ cardId, enabled = true }: { cardId: string; enabled?: boolean }) {
  const client = useApiClient();
  return useQuery({
    queryKey: ['cards', cardId, 'pin'],
    enabled,
    gcTime: 0,
    staleTime: Infinity,
    queryFn: async ({ signal }) => {
      const pin = await withWallesterEncryption('PIN', async (key) => {
        const res = await client.POST('/cards/{id}/pin', {
          params: { path: { id: cardId } },
          signal,
          body: {
            key,
            issuerEncryptionType: 'RSA',
          },
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        });

        if (!res.data) {
          throw new Error('No data');
        }

        return res.data.pin;
      });

      return { pin };
    },
  });
}

export function useCardActivateMutation(cardId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cards', 'activate'],
    mutationFn: (body: ApiCardActivateRequest) =>
      client
        .POST('/cards/{id}/activate', {
          body,
          params: { path: { id: cardId } },
          headers: {
            'Idempotency-Key': cardId,
          },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['cards'],
      }),
  });
}

export function useCardTerminateMutation({
  cardId,
  idempotencyKey,
}: {
  cardId: string;
  idempotencyKey: string;
}) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cards', cardId, 'terminate'],
    mutationFn: () =>
      client
        .POST('/cards/{id}/terminate', {
          params: { path: { id: cardId } },
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['cards'],
      }),
  });
}

export function useCardFreezeMutation(cardId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cards', cardId, 'freeze'],
    mutationFn: () =>
      client
        .POST('/cards/{id}/freeze', {
          params: { path: { id: cardId } },
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['cards'],
      }),
  });
}

export function useCardRenameMutation(cardId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cards', cardId, 'rename'],
    mutationFn: (body: ApiCardRenameRequest) =>
      client
        .PUT('/cards/{id}/rename', {
          params: { path: { id: cardId } },
          body,
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['cards'],
      }),
  });
}

export function useCardUnfreezeMutation(cardId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cards', cardId, 'unfreeze'],
    mutationFn: () =>
      client
        .POST('/cards/{id}/unfreeze', {
          params: { path: { id: cardId } },
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['cards'],
      }),
  });
}

export function useCardUpdateSecurityMutation(cardId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cards', cardId, 'update-security'],
    mutationFn: (body: ApiCardUpdateSecurityRequest) =>
      client
        .PUT('/cards/{id}/security', {
          params: { path: { id: cardId } },
          body,
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['cards'],
      }),
  });
}

export function useCardUpdateSpendingLimitMutation(cardId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cards', cardId, 'update-spending-limit'],
    mutationFn: (body: ApiCardUpdateSpendingLimitRequest) =>
      client
        .PUT('/cards/{id}/spendingLimit', {
          params: { path: { id: cardId } },
          body,
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['cards'],
      }),
  });
}

export function useCardUpdateSpendingStrategyMutation(cardId: string) {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ['cards', cardId, 'update-spending-strategy'],
    mutationFn: (body: ApiCardUpdateSpendingStrategyRequest) =>
      client
        .PUT('/cards/{id}/spendingStrategy', {
          params: { path: { id: cardId } },
          body,
          headers: {
            'Idempotency-Key': createIdempotencyKey(),
          },
        })
        .then(mapResponse),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: ['cards'],
      }),
  });
}
